import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getPrintBooks } from '../services/print_service'
import { generateStoresReport } from '../services/stores_report_service'
import './PublishingReport.css'

const reportTypes = [
  {
    value: 'summary',
    label: 'Summary',
    description: 'Operational totals for review and printing.',
  },
  {
    value: 'list',
    label: 'List',
    description: 'Detailed store records downloaded as Excel.',
  },
]

const periods = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This week' },
  { value: 'last-week', label: 'Last week' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'this-year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
]

const entities = [
  {
    value: 'inventory',
    label: 'Inventory position',
    hint: 'Current quantities, stock value, and low stock titles',
  },
  {
    value: 'stock-movements',
    label: 'Stock movements',
    hint: 'Books received into and issued from stores',
  },
  {
    value: 'printing',
    label: 'Printing',
    hint: 'Print requests, quantities, status, and costs',
  },
  {
    value: 'releases',
    label: 'Released books',
    hint: 'Orders and quantities released from stores',
  },
]

const bookTypes = [
  { value: 'all', label: 'All book types' },
  { value: 'published_book', label: 'Published books' },
  { value: 'exercise_book', label: 'Exercise books' },
]
const directions = [
  { value: 'all', label: 'All movements' },
  { value: 'IN', label: 'Stock in' },
  { value: 'OUT', label: 'Stock out' },
]
const printStatuses = [
  { value: 'all', label: 'All print statuses' },
  { value: 'requested', label: 'Requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'printed', label: 'Printed' },
]

const getBookId = (book) => book?.bookid || book?.book_id || book?.id
const getBookTitle = (book) =>
  book?.book_title || book?.bookTitle || book?.title || book?.name
const formatFieldLabel = (value) =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
const formatReportValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'number') return value.toLocaleString()
  return String(value)
}

function SummaryData({ data }) {
  return (
    <dl className='generated-report__metrics'>
      {Object.entries(data || {}).map(([key, value]) => (
        <div key={key}>
          <dt>{formatFieldLabel(key)}</dt>
          <dd>{formatReportValue(value)}</dd>
        </div>
      ))}
    </dl>
  )
}

function StoresReport() {
  const [reportType, setReportType] = useState('summary')
  const [period, setPeriod] = useState('this-month')
  const [entity, setEntity] = useState('inventory')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [bookType, setBookType] = useState('all')
  const [selectedBookIds, setSelectedBookIds] = useState([])
  const [bookSearch, setBookSearch] = useState('')
  const [direction, setDirection] = useState('all')
  const [printStatus, setPrintStatus] = useState('all')
  const [books, setBooks] = useState([])
  const [booksLoading, setBooksLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedReport, setGeneratedReport] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    setBooksLoading(true)
    setError('')
    const request =
      bookType === 'all'
        ? Promise.all(
            bookTypes
              .slice(1)
              .map((item) => getPrintBooks(item.value, controller.signal)),
          ).then((lists) => {
            const uniqueBooks = new Map()
            lists.flat().forEach((item) => {
              uniqueBooks.set(String(getBookId(item)), item)
            })
            return [...uniqueBooks.values()]
          })
        : getPrintBooks(bookType, controller.signal)

    request
      .then(setBooks)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setBooksLoading(false)
      })
    return () => controller.abort()
  }, [bookType])

  const selectedType = reportTypes.find((item) => item.value === reportType)
  const selectedPeriod = periods.find((item) => item.value === period)
  const selectedEntity = entities.find((item) => item.value === entity)
  const selectedBookType = bookTypes.find((item) => item.value === bookType)
  const selectedBooks = books.filter((item) =>
    selectedBookIds.includes(String(getBookId(item))),
  )
  const visibleBooks = books.filter((item) =>
    String(getBookTitle(item) || '')
      .toLowerCase()
      .includes(bookSearch.trim().toLowerCase()),
  )
  const booksScope = !selectedBooks.length
    ? 'All books'
    : selectedBooks.length <= 3
      ? selectedBooks.map(getBookTitle).join(', ')
      : `${selectedBooks.length} books`
  const selectedDirection = directions.find((item) => item.value === direction)
  const selectedPrintStatus = printStatuses.find(
    (item) => item.value === printStatus,
  )
  const scope = [
    selectedBookType.label,
    booksScope,
    ...(entity === 'stock-movements' ? [selectedDirection.label] : []),
    ...(entity === 'printing' ? [selectedPrintStatus.label] : []),
  ].join(' · ')

  async function submit(event) {
    event.preventDefault()
    setError('')
    setSuccess('')
    setGeneratedReport(null)
    if (period === 'custom' && (!fromDate || !toDate)) {
      setError('Choose both custom period dates.')
      return
    }
    if (fromDate && toDate && fromDate > toDate) {
      setError('The start date cannot be after the end date.')
      return
    }

    setIsGenerating(true)
    try {
      const data = await generateStoresReport({
        report_type: reportType,
        period,
        ...(period === 'custom'
          ? { from_date: fromDate, to_date: toDate }
          : {}),
        entity,
        book_type: bookType,
        book_ids: selectedBookIds.map(Number),
        direction: entity === 'stock-movements' ? direction : 'all',
        print_status: entity === 'printing' ? printStatus : 'all',
      })
      if (reportType === 'list') {
        setSuccess('The Excel stores report has been downloaded.')
      } else {
        setGeneratedReport({
          data,
          entity: selectedEntity.label,
          reportType: selectedType.label,
          period:
            period === 'custom'
              ? `${fromDate} to ${toDate}`
              : selectedPeriod.label,
          scope,
          generatedAt: new Date().toLocaleString(),
        })
      }
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsGenerating(false)
    }
  }

  function chooseEntity(value) {
    setEntity(value)
    setError('')
    setSuccess('')
    setGeneratedReport(null)
  }

  function toggleBook(bookId) {
    setSelectedBookIds((current) =>
      current.includes(bookId)
        ? current.filter((id) => id !== bookId)
        : [...current, bookId],
    )
  }

  function selectVisibleBooks() {
    setSelectedBookIds((current) => [
      ...new Set([
        ...current,
        ...visibleBooks.map((item) => String(getBookId(item))),
      ]),
    ])
  }

  return (
    <div className='publishing-report-page'>
      <nav className='publishing-report__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <span>Reports</span>
        <span aria-hidden='true'>/</span>
        <strong>Stores</strong>
      </nav>

      <header className='publishing-report__header'>
        <div>
          <span className='app-content__eyebrow'>Stores reports</span>
          <h1>Build a stores report</h1>
          <p>Track inventory, printing, stock movement, and book releases.</p>
        </div>
        <div className='publishing-report__mark' aria-hidden='true'>
          <span>STORES</span>
          <strong>03</strong>
        </div>
      </header>

      <form className='report-builder' onSubmit={submit}>
        <section
          className='report-builder__section'
          aria-labelledby='stores-type-title'
        >
          <div className='report-builder__section-heading'>
            <span>01</span>
            <div>
              <h2 id='stores-type-title'>Report type</h2>
              <p>Choose a printable summary or detailed Excel register.</p>
            </div>
          </div>
          <div className='report-type-options'>
            {reportTypes.map((item) => (
              <label
                className={`report-type-option ${reportType === item.value ? 'is-selected' : ''}`}
                key={item.value}
              >
                <input
                  type='radio'
                  name='reportType'
                  value={item.value}
                  checked={reportType === item.value}
                  onChange={(event) => setReportType(event.target.value)}
                />
                <span
                  className='report-type-option__indicator'
                  aria-hidden='true'
                />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='stores-period-title'
        >
          <div className='report-builder__section-heading'>
            <span>02</span>
            <div>
              <h2 id='stores-period-title'>Period</h2>
              <p>Select the operational period to report.</p>
            </div>
          </div>
          <div className='report-periods'>
            {periods.map((item) => (
              <button
                className={period === item.value ? 'is-selected' : ''}
                type='button'
                key={item.value}
                onClick={() => setPeriod(item.value)}
                aria-pressed={period === item.value}
              >
                {item.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className='report-custom-period'>
              <label>
                From
                <input
                  type='date'
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(event) => setFromDate(event.target.value)}
                  required
                />
              </label>
              <span aria-hidden='true'>to</span>
              <label>
                To
                <input
                  type='date'
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(event) => setToDate(event.target.value)}
                  required
                />
              </label>
            </div>
          )}
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='stores-entity-title'
        >
          <div className='report-builder__section-heading'>
            <span>03</span>
            <div>
              <h2 id='stores-entity-title'>Stores activity</h2>
              <p>Select the inventory operation to report.</p>
            </div>
          </div>
          <div className='report-entities'>
            {entities.map((item) => (
              <label
                className={`report-entity ${entity === item.value ? 'is-selected' : ''}`}
                key={item.value}
              >
                <input
                  type='radio'
                  name='entity'
                  value={item.value}
                  checked={entity === item.value}
                  onChange={(event) => chooseEntity(event.target.value)}
                />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.hint}</small>
                </span>
                <span className='report-entity__check' aria-hidden='true'>
                  ✓
                </span>
              </label>
            ))}
          </div>
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='stores-options-title'
        >
          <div className='report-builder__section-heading'>
            <span>04</span>
            <div>
              <h2 id='stores-options-title'>Report options</h2>
              <p>Narrow the register or include all available books.</p>
            </div>
          </div>
          <div className='report-entity-options'>
            <label>
              Book type
              <select
                value={bookType}
                onChange={(event) => {
                  setBookType(event.target.value)
                  setSelectedBookIds([])
                  setBookSearch('')
                }}
              >
                {bookTypes.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <fieldset className='report-book-picker' disabled={booksLoading}>
              <legend>
                Books{' '}
                <span>
                  {booksLoading
                    ? 'Loading books...'
                    : selectedBookIds.length
                      ? `${selectedBookIds.length} selected`
                      : 'None selected = all books'}
                </span>
              </legend>
              <div className='report-book-picker__toolbar'>
                <input
                  type='search'
                  placeholder='Search books'
                  value={bookSearch}
                  onChange={(event) => setBookSearch(event.target.value)}
                  aria-label='Search books'
                />
                <button
                  type='button'
                  onClick={selectVisibleBooks}
                  disabled={!visibleBooks.length}
                >
                  Select shown
                </button>
                <button
                  type='button'
                  onClick={() => setSelectedBookIds([])}
                  disabled={!selectedBookIds.length}
                >
                  Clear
                </button>
              </div>
              <ul className='report-book-picker__list'>
                {visibleBooks.map((item) => {
                  const bookId = String(getBookId(item))
                  return (
                    <li key={bookId}>
                      <label>
                        <input
                          type='checkbox'
                          checked={selectedBookIds.includes(bookId)}
                          onChange={() => toggleBook(bookId)}
                        />
                        <span>{getBookTitle(item)}</span>
                      </label>
                    </li>
                  )
                })}
                {!booksLoading && !visibleBooks.length && (
                  <li className='report-book-picker__empty'>
                    No books match your search.
                  </li>
                )}
              </ul>
            </fieldset>
            {entity === 'stock-movements' && (
              <label>
                Direction
                <select
                  value={direction}
                  onChange={(event) => setDirection(event.target.value)}
                >
                  {directions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {entity === 'printing' && (
              <label>
                Print status
                <select
                  value={printStatus}
                  onChange={(event) => setPrintStatus(event.target.value)}
                >
                  {printStatuses.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </section>

        {error && (
          <div className='report-builder__error' role='alert'>
            {error}
          </div>
        )}
        {success && (
          <div className='report-builder__success' role='status'>
            {success}
          </div>
        )}
        <footer className='report-builder__footer'>
          <div>
            <span>Ready to generate</span>
            <strong>
              {selectedType.label} ·{' '}
              {period === 'custom'
                ? `${fromDate || 'From date'} to ${toDate || 'To date'}`
                : selectedPeriod.label}{' '}
              · {selectedEntity.label} · {scope}
            </strong>
          </div>
          <button type='submit' disabled={isGenerating}>
            {isGenerating
              ? 'Generating...'
              : reportType === 'list'
                ? 'Generate & download'
                : 'Generate report'}{' '}
            {!isGenerating && <span aria-hidden='true'>→</span>}
          </button>
        </footer>
      </form>

      {generatedReport && (
        <section className='generated-report' aria-live='polite'>
          <div>
            <span className='app-content__eyebrow'>Stores report ready</span>
            <h2>
              {generatedReport.entity} {generatedReport.reportType}
            </h2>
            <p>
              {generatedReport.scope} · {generatedReport.period} · Generated{' '}
              {generatedReport.generatedAt}
            </p>
            <SummaryData data={generatedReport.data} />
          </div>
          <button type='button' onClick={() => window.print()}>
            Print report
          </button>
        </section>
      )}
    </div>
  )
}

export default StoresReport
